import clsx from "clsx"
import { type RefObject, useRef } from "react"

import { Badge } from "../Badge"
import { BaseInput } from "../BaseInput"
import { Text } from "../Text"
import styles from "./TagFieldHeader.module.css"
import {
  type LastItemLocation,
  useHandleKeyboardEntries,
  useTagFieldHeader,
} from "./useTagFieldHeader"

interface TagFieldHeaderProps {
  selectedValues: Set<string>
  searchText: string
  setSearchText: (searchText: string) => void
  open: boolean
  setOpen: (open: boolean) => void
  isDisabled: boolean | undefined
  onSelectedValuesChange: (selectedValues: Set<string>) => void
  onCreateTag: () => void
  fieldRef: RefObject<HTMLDivElement>
}

export function TagFieldHeader(props: TagFieldHeaderProps) {
  const tagsContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    selectedValues,
    searchText,
    setSearchText,
    open,
    setOpen,
    isDisabled,
    onCreateTag,
    onSelectedValuesChange,
  } = props

  const { displayedSelectedValues, extraTags, removeSelectedValue } =
    useTagFieldHeader({
      selectedValues,
      tagsContainerRef,
      open,
      onSelectedValuesChange,
    })

  const { onKeyDown } = useHandleKeyboardEntries({
    inputRef,
    searchText,
    setSearchText,
    setOpen,
    onCreateTag,
    removeSelectedValue,
  })

  return (
    <div
      className={clsx(styles.inputContainer, open && styles.invisibleBorder)}
      ref={tagsContainerRef}
    >
      <TagFieldHeaderContent
        displayedTags={Array.from(displayedSelectedValues)}
        extraTags={open ? 0 : extraTags}
        removeSelectedValue={removeSelectedValue}
        open={open}
      />
      <div className={styles.input}>
        <BaseInput
          ref={inputRef}
          inputMode="text"
          onFocus={() => setOpen(true)}
          placeholder="Add new tag here"
          value={searchText}
          onKeyDown={onKeyDown}
          onValueChange={(value) => setSearchText(value)}
          disabled={isDisabled}
        />
      </div>
    </div>
  )
}

interface TagsContainerProps {
  displayedTags: string[]
  extraTags: number
  removeSelectedValue: (valueToDelete: string | LastItemLocation) => void
  open: boolean
}

export function TagFieldHeaderContent({
  displayedTags,
  extraTags,
  removeSelectedValue,
  open,
}: TagsContainerProps) {
  return (
    <>
      {displayedTags?.map((tag) => (
        <Badge
          title={tag}
          key={tag}
          color="purple"
          size="medium"
          maxWidth={open ? undefined : 150}
          onClose={() => removeSelectedValue(tag)}
          selected
        />
      ))}
      {extraTags > 0 && (
        <div className={styles.extraTagContainer}>
          <Text color="grey11" variant="small">
            {`+ ${extraTags}`}
          </Text>
        </div>
      )}
    </>
  )
}
